// Libraries
import React from 'react';
import { useContext } from 'react';
import { Button } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormData } from '../../CreateQuestionnaire';
import PreviewForm from './PreviewForm';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import styles from './Preview.module.css';

export const organizeFormGroups = (formData) => {
  const schema = formData.schema || {};
  const sortedFormData = [];
  const groups = [];
  let currentGroup = [];
  const visited = new Set();

  const pushNode = (node, parentId, depth) => {
    if (node.formType === undefined || node.formType === null)
      node.formType = true;
    const parent = parentId ? schema[parentId] : undefined;
    if (node.formType) {
      sortedFormData.push({
        type: 'form',
        data: node,
        connected: false,
        depth
      });
    } else {
      sortedFormData.push({
        type: 'connectedForm',
        data: node,
        connected: true,
        connectedData: parent,
        depth
      });
    }
  };

  const traverse = (id, parentId, depth) => {
    if (!id || visited.has(id)) return;
    const node = schema[id];
    if (!node) return;
    visited.add(id);
    pushNode(node, parentId, depth);
    (node.groupsConnectedTo || []).forEach((child) => {
      if (child && child.id) traverse(child.id, id, depth + 1);
    });
  };

  traverse(formData.startingGroupId, null, 0);

  Object.keys(schema).forEach((id) => {
    if (visited.has(id)) return;
    visited.add(id);
    pushNode(schema[id], schema[id].previousConnections?.[0], 1);
  });

  for (let i = 0; i < sortedFormData.length; i++) {
    const form = sortedFormData[i];

    if (form.type === 'form') {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }

      currentGroup = [form];
    } else {
      currentGroup.push(form);
    }
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
};

function Preview({ data }) {
  const FORM_DATA = useContext(FormData);
  const t = useAutoTranslation();
  let formData;
  if (Object.keys(FORM_DATA).length > 0) {
    formData = FORM_DATA[0];
  } else {
    formData = data.config;
  }

  const schema = formData.schema || {};

  const getChildren = (node) =>
    (node.groupsConnectedTo || [])
      .map((child) => (child && child.id ? schema[child.id] : null))
      .filter(Boolean);

  const renderNode = (node, visited) => {
    if (!node || visited.has(node.id)) return null;
    visited.add(node.id);
    const isForm = node.formType !== false;
    const children = getChildren(node);

    if (isForm) {
      return (
        <div key={node.id} className={styles.subFormBlock}>
          <PreviewForm data={node} subForm />
          {children.length > 0 && (
            <div className={styles.subFormBody}>
              {children.map((child) => renderNode(child, visited))}
            </div>
          )}
        </div>
      );
    }

    return (
      <React.Fragment key={node.id}>
        <PreviewForm data={node} connected />
        {children.map((child) => renderNode(child, visited))}
      </React.Fragment>
    );
  };

  const root = schema[formData.startingGroupId];
  const visited = new Set();
  let rootChildren = [];
  if (root) {
    visited.add(root.id);
    rootChildren = getChildren(root);
  }

  return (
    <div className={styles.previewPage}>
      <div className={styles.previewBanner}>
        <InfoOutlinedIcon fontSize="small" />
        <span>
          {t(
            'Preview: this is how the form will appear to the people filling it out.'
          )}
        </span>
      </div>

      <div className={styles.previewContainer}>
        {root && (
          <>
            <PreviewForm data={root} />
            {rootChildren.map((child) => renderNode(child, visited))}
          </>
        )}
      </div>

      <div className={styles.previewSubmitRow}>
        <Button variant="contained" color="secondary" size="large">
          {t('Submit')}
        </Button>
      </div>
    </div>
  );
}

export default Preview;
